// Lapisan anotasi CCTV: menampilkan ROI (poligon) dan garis hitung per arah,
// sekaligus menambahkannya saat mode sunting (permintaan Andra 29 Okt 2026,
// #975 poligon ROI, #976 garis hitung dan arah).
//
// Koordinat TERNORMALKAN 0-1, asal kiri-atas, dipetakan ke kotak sel (asumsi
// video mengisi penuh kotak 16:9 - sama dengan objectFit cover pada stream 16:9).
// Tanpa sunting: pointer-events none supaya klik sel tetap membuka perbesar.

import { useEffect, useMemo, useState } from 'react';
import { roiEfektif, poligonKamera } from '../data/cameraRoi';
import { bacaKamera, simpanKamera, hapusKamera } from '../lib/roiStore';

const CYAN = '#00d4ff';
const HIJAU = '#00ff88';
const KUNING = '#ffd60a';
const SW = 0.0035;

function labelGaris(nama, i) {
  return nama || String.fromCharCode(65 + i);
}

export default function CctvAnotasi({ camId, editing, onSelesai }) {
  const [override, setOverride] = useState(() => bacaKamera(camId));
  const [draft, setDraft] = useState([]);

  useEffect(() => { setOverride(bacaKamera(camId)); }, [camId]);
  useEffect(() => { setDraft([]); }, [editing, camId]);

  const data = useMemo(() => roiEfektif(camId, override), [camId, override]);
  const pola = data.poligon || [];
  const garis = data.garis || [];

  const tambahTitik = (e) => {
    if (!editing) return;
    const r = e.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    setDraft((d) => [...d, [Number(x.toFixed(4)), Number(y.toFixed(4))]]);
  };

  // Garis: setiap dua titik menjadi satu garis. Poligon: ditutup lewat tombol.
  useEffect(() => {
    if (editing !== 'garis' || draft.length < 2) return;
    const titik = [draft[0], draft[1]];
    setOverride((o) => {
      const dasar = o || {};
      const g = [...(dasar.garis || [])];
      g.push({ nama: labelGaris(null, g.length), titik });
      const baru = { ...dasar, garis: g };
      simpanKamera(camId, baru);
      return baru;
    });
    setDraft([]);
  }, [draft, editing, camId]);

  const tutupPoligon = () => {
    if (draft.length < 3) return;
    setOverride((o) => {
      const dasar = o || {};
      const p = [...(dasar.poligon || []), draft];
      const baru = { ...dasar, poligon: p };
      simpanKamera(camId, baru);
      return baru;
    });
    setDraft([]);
  };

  const hapusSemua = () => {
    hapusKamera(camId);
    setOverride(null);
    setDraft([]);
  };

  return (
    <div
      className="absolute inset-0 z-20"
      style={{ pointerEvents: editing ? 'auto' : 'none', cursor: editing ? 'crosshair' : 'default' }}
      onClick={tambahTitik}
    >
      <svg viewBox="0 0 1 1" preserveAspectRatio="none" className="w-full h-full">
        {pola.map((c, i) => (
          <polygon key={`p${i}`} points={c.map((p) => `${p[0]},${p[1]}`).join(' ')}
            fill="rgba(0,212,255,0.13)" stroke={CYAN} strokeWidth={SW} vectorEffect="non-scaling-stroke" />
        ))}
        {draft.length >= 2 && editing === 'roi' && (
          <polyline points={draft.map((p) => `${p[0]},${p[1]}`).join(' ')}
            fill="none" stroke={KUNING} strokeWidth={SW} vectorEffect="non-scaling-stroke" strokeDasharray="0.01 0.006" />
        )}
        {draft.map((p, i) => (
          <circle key={`d${i}`} cx={p[0]} cy={p[1]} r={0.008} fill={KUNING} />
        ))}
        {garis.map((g, i) => {
          const [a, b] = g.titik;
          const mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2;
          const dx = b[0] - a[0], dy = b[1] - a[1];
          const len = Math.hypot(dx, dy) || 1;
          const ux = dx / len, uy = dy / len;
          const hx = mx + ux * 0.03, hy = my + uy * 0.03;     // ujung panah arah
          return (
            <g key={`g${i}`}>
              <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} stroke={HIJAU}
                strokeWidth={SW} vectorEffect="non-scaling-stroke" />
              <line x1={mx} y1={my} x2={hx} y2={hy} stroke={HIJAU}
                strokeWidth={SW} vectorEffect="non-scaling-stroke" strokeDasharray="0.008 0.004" />
              <circle cx={hx} cy={hy} r={0.01} fill={HIJAU} />
              <text x={hx} y={hy - 0.02} fontSize={0.05} fill={HIJAU} textAnchor="middle">
                {labelGaris(g.nama, i)}
              </text>
            </g>
          );
        })}
      </svg>

      {editing && (
        <div className="absolute bottom-1 left-1 right-1 z-30 flex flex-wrap gap-1 items-center"
          style={{ pointerEvents: 'auto' }}>
          <span className="text-[8px] font-mono px-1.5 py-0.5 rounded"
            style={{ background: 'rgba(0,0,0,0.6)', color: KUNING, border: `1px solid ${KUNING}55` }}>
            {editing === 'roi' ? `POLIGON: ${draft.length} titik` : `GARIS: titik ${draft.length}/2`}
          </span>
          {editing === 'roi' && (
            <button onClick={(e) => { e.stopPropagation(); tutupPoligon(); }} disabled={draft.length < 3}
              className="text-[8px] font-black px-2 py-0.5 rounded disabled:opacity-40"
              style={{ background: 'rgba(0,255,136,0.15)', color: HIJAU, border: `1px solid ${HIJAU}55` }}>
              TUTUP POLIGON
            </button>
          )}
          <button onClick={(e) => { e.stopPropagation(); setDraft([]); }}
            className="text-[8px] font-black px-2 py-0.5 rounded"
            style={{ background: 'rgba(255,214,10,0.15)', color: KUNING, border: `1px solid ${KUNING}55` }}>
            BATAL TITIK
          </button>
          <button onClick={(e) => { e.stopPropagation(); hapusSemua(); }}
            className="text-[8px] font-black px-2 py-0.5 rounded"
            style={{ background: 'rgba(255,45,85,0.15)', color: '#ff2d55', border: '1px solid rgba(255,45,85,0.4)' }}>
            HAPUS
          </button>
          <button onClick={(e) => { e.stopPropagation(); onSelesai && onSelesai(); }}
            className="text-[8px] font-black px-2 py-0.5 rounded ml-auto"
            style={{ background: 'rgba(0,212,255,0.15)', color: CYAN, border: `1px solid ${CYAN}55` }}>
            SELESAI
          </button>
        </div>
      )}
    </div>
  );
}

export { poligonKamera };
